<template>
    <!-- Custom elements -->
    <div class="px-[20px] lg:px-[40px]">
        <div>
            <placement-value
                :label="$t('MODEL')"
                :selected-label="selectedModelName"
                :has-error-state="hasModelError"
            />

            <div class="flex flex-wrap gap-2.5">
                <div
                    v-for="option in modelOptions"
                    :key="option.value"
                    v-auto-animate
                    class="h-[80px] w-[80px]"
                >
                    <swatch-button
                        class="border border-transparent hover:border-black duration-200 transition-colors"
                        :show-checkmark="false"
                        :input-name="option.name"
                        :input-value="option.value"
                        :input-color="option.color"
                        :input-image="option.image"
                        :selected="option.selected || option.groupSelected"
                        :variant="option.variant"
                        :debug-value="option.debugValue"
                        :label="option.label"
                        :no-thanks="option.placeholder"
                        :price="option.price"
                        :is-disabled="option.isDisabled"
                        :disabled-reason="option.disabledReason"
                        :disabled-reason-body="option.disabledReasonBody"
                        @valueChange="handleFinishSelect(option)"
                    />
                </div>
            </div>
        </div>

        <div>
            <placement-value
                :label="$t('SELECT_ORIENTATION')"
                :has-error-state="hasOrientationError"
            />

            <div class="flex flex-wrap gap-2.5"
                :class="{
                    'border border-red p-1 -mx-1': hasOrientationError,
                }"
            >
                <div
                    v-for="option in orientationOptions"
                    :key="option.value"
                    class="flex-1"
                    v-auto-animate
                >
                    <black-white-button
                        @click="handleOrientationSelect(option)"
                        class="w-full text-[16px] font-semibold"
                        :is-selected="option.selected"
                    >
                        {{option.label}}
                    </black-white-button>
                </div>
            </div>
        </div>

        <div v-auto-animate>
            <div class="my-8 border-t border-gray-light -mx-12"></div>

            <placement-value
                :label="$t('SELECT_SPECCOMP')"
                :has-error-state="hasLocalError"
            />

            <p
                v-if="selectedWedgeOptions?.length > 1"
                class="leading-tight mb-3 text-[14px]"
                :class="{
                    'text-red-error': hasLocalError,
                }"
            >
                {{$t('SELECT_SPECCOMP_INFO')}}
            </p>

            <div class="grid grid-cols-4 gap-2.5 mt-0"
                :class="{
                    'border border-red p-1 -mx-1': hasLocalError,
                }"
            >
                <div
                    v-for="option in wedgeOptions"
                    :key="option.value"
                    v-auto-animate
                >
                    <black-white-button
                        @click="handleWedgeSelect(option)"
                        class="w-full text-[14px] tracking-[.5px] font-semibold"
                        :is-selected="option.selected"
                        :has-error-state="hasLocalError"
                    >
                        {{option.label}}
                    </black-white-button>
                </div>
            </div>
        </div>

        <!-- Standard placements -->
        <div v-for="placement in visiblePlacements"
            :key="placement.code"
            class="relative"
        >
            <placement-value
                :label="placement.name"
                :tooltip="getPlacementTooltip(placement)"
                :selected-label="placement.selectedName"
            />

            <step-options-standard-buttons v-if="placement.groupType === 'standard-buttons'"
                :item-data="itemData"
                :placement="placement"
            ></step-options-standard-buttons>

            <step-options-dropdowns v-if="placement.groupType === 'dropdowns'"
                :placement="placement"
            ></step-options-dropdowns>
        </div>
        <div class="pb-[24px]"></div>
    </div>
</template>

<script>
    import PlacementValue from './PlacementValue.vue';
    import BlackWhiteButton from './buttons/BlackWhiteButton.vue';
    import StepOptionsStandardButtons from './buttons/StepOptionsStandardButtons.vue';
    import StepOptionsDropdowns from './StepOptionsDropdowns.vue';
    import SwatchButton from './buttons/SwatchButton.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        emits: ['applySelections'],

        components: {
            PlacementValue,
            BlackWhiteButton,
            StepOptionsDropdowns,
            StepOptionsStandardButtons,
            SwatchButton,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                selectedModel: null,
                selectedOrientation: null,
            };
        },

        computed: {
            hasLocalError() {
                return this.isDirty && this.selectedWedgeOptions?.length === 0;
            },

            hasModelError() {
                return this.isDirty && !this.selectedModelOption;
            },

            hasOrientationError() {
                return this.isDirty && !this.selectedOrientationOption;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                const visible = placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        selectedName: selected ? this.viewController.componentNames(selected).join(' ') : '',
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });

                return visible;
            },

            hasSets() {
                return this.itemData.custom['v-sets'];
            },

            allProducts() {
                return this.customizerStore.allProducts;
            },

            selectedProducts() {
                return this.customizerStore.selectedProducts;
            },

            isCompleteSelection() {
                return this.customizerStore.selectedProducts.length > 0;
            },

            availableModels() {
                const models = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const p of this.allProducts) {
                    const masterProduct = p.custom?.['t-master-product'];

                    if (masterProduct) {
                        if (!models.some((x) => x.masterProduct === masterProduct)) {
                            models.push({
                                masterProduct,
                                masterProductName: p.name,
                            });
                        }
                    }
                }

                models.sort((a, b) => {
                    const aN = +a.finishOrder;
                    const bN = +b.finishOrder;

                    return aN - bN;
                });

                return models;
            },

            modelOptions() {
                const buttons = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const f of this.availableModels) {
                    let selected = false;

                    if (this.selectedModel) {
                        selected = this.selectedModel.value === f.masterProduct;
                    } else {
                        selected = this.selectedProducts.some((x) => x.custom?.['t-master-product'] === f.masterProduct);
                    }

                    const product = this.allProducts.find((x) => x.custom?.['t-master-product'] === f.masterProduct);
                    let image = '';

                    if (product) {
                        const modelCode = f.masterProduct.replace(':', '-');
                        // let finish = product.custom['t-finish'];

                        // if (finish == null || finish.length === 0) {
                        //     finish = 'Nickel';
                        // }

                        // image = `${this.viewController.blueprint.imageRootUrl}Finish${f.masterProduct.replace(':', '-')}${finish}`;
                        image = `${this.viewController.blueprint.imageRootUrl}CZ-Putter-${modelCode}-2.png`;
                    }

                    buttons.push({
                        name: f.masterProductName,
                        value: f.masterProduct,
                        variant: 'medium',
                        color: '#f6f6f6',
                        image,
                        selected,
                    });
                }

                return buttons;
            },

            availableOrientations() {
                const orientations = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const p of this.allProducts) {
                    const orientation = p.custom?.['t-orientation'];
                    const orientationName = p.custom?.['t-orientation-name'];

                    if (orientation && orientationName) {
                        if (!orientations.some((x) => x.orientation === orientation)) {
                            orientations.push({
                                orientation,
                                orientationName,
                            });
                        }
                    }
                }

                return orientations;
            },

            orientationOptions() {
                const buttons = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const f of this.availableOrientations) {
                    let selected = null;

                    if (this.selectedOrientation) {
                        selected = this.selectedOrientation.value === f.orientation;
                    } else {
                        selected = this.selectedProducts.some((x) => x.custom?.['t-orientation'] === f.orientation);
                    }

                    buttons.push({
                        name: f.orientationName,
                        value: f.orientation,
                        label: f.orientationName,
                        variant: 'medium',
                        selected,
                    });
                }

                return buttons;
            },

            availableClubs() {
                const selectedOrientation = this.viewController.blueprint.custom['t-orientation'];

                const setcomps = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const p of this.allProducts) {
                    if (p.custom?.['t-orientation'] === selectedOrientation) {
                        const setcomp = p.custom?.['t-setcomp'];
                        const setcompName = p.custom?.['t-setcomp-name'];

                        if (setcomp && setcompName) {
                            if (!setcomps.some((x) => x.setcomp === setcomp)) {
                                setcomps.push({
                                    setcomp,
                                    setcompName,
                                });
                            }
                        }
                    }
                }

                setcomps.sort((a, b) => {
                    if (a.setcomp < b.setcomp) {
                        return -1;
                    }

                    if (a.setcomp > b.setcomp) {
                        return 1;
                    }

                    return 0;
                });

                return setcomps;
            },

            wedgeOptions() {
                const buttons = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const f of this.availableClubs) {
                    const selected = this.selectedProducts.some((x) => x.custom?.['t-setcomp'] === f.setcomp);

                    buttons.push({
                        name: f.setcompName,
                        value: f.setcomp,
                        label: f.setcompName,
                        variant: 'medium',
                        selected,
                    });
                }

                return buttons;
            },

            selectedWedgeOptions() {
                return this.wedgeOptions.filter((x) => x.selected);
            },

            selectedModelOption() {
                return this.modelOptions.find((x) => x.selected);
            },

            selectedOrientationOption() {
                return this.orientationOptions.find((x) => x.selected);
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            showGroupOverlay() {
                return this.appStore.showGroupOverlay;
            },

            selectedModelName() {
                return this.viewController.blueprint.name;
            },
        },

        watch: {
            viewController() {
                // Controller could change when we reset the panel, so this is useful to reload the values.

                this.setSelectedOrientation();
            },

            hasLocalError(val) {
                if (val) {
                    this.customizerStore.addPanelToErrorState('Specifications');
                } else {
                    this.customizerStore.removePanelFromErrorState('Specifications');
                }
            },

            selectedProducts: {
                handler() {
                    // if (!this.isDirty) {
                    //     return;
                    // }
                    // this.hasLocalError = this.selectedWedgeOptions?.length === 0;
                },
                deep: true,
            },

            showGroupOverlay(val) {
                if (val && this.currentGroupName === 'Specifications') {
                    this.setSelectedOrientation();
                }
            },
        },

        mounted() {
            this.setSelectedOrientation();
        },

        methods: {
            handleFinishSelect(option) {
                this.selectedModel = option;

                this.customizerStore.changeFinish(option.value);
            },

            setSelectedOrientation() {
                let currentOrientation = this.viewController.blueprint.custom['t-orientation'];

                if (this.selectedProducts.length === 0) {
                    currentOrientation = this.viewController.blueprint.custom['t-default-orientation'];

                    if (this.customizerStore.options.initialOrientation) {
                        currentOrientation = this.customizerStore.options.initialOrientation;
                    }
                }

                this.selectedOrientation = this.orientationOptions.find((x) => x.value === currentOrientation);
            },
            getPlacementVariant(placement) {
                return placement?.custom?.['v-placement-variant'];
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            handleOrientationSelect(option) {
                this.selectedOrientation = option;

                this.customizerStore.changeOrientation(option.value);
            },

            handleWedgeSelect(option) {
                const orientation = this.orientationOptions.find((x) => x.selected).value;
                const length = option.value;

                const product = this.allProducts.find((x) => {
                    if (x.custom?.['t-orientation'] === orientation
                        && x.custom?.['t-setcomp'] === length) {
                        return true;
                    }

                    return false;
                });

                if (product) {
                    // Single select.
                    this.customizerStore.clearSelectedProducts();

                    this.customizerStore.toggleSelectedProduct(product);

                    this.handleApplySelections();
                }
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                if (this.hasLocalError) {
                    this.customizerStore.addPanelToErrorState('Specifications');
                    return;
                }

                this.$emit('applySelections', false);
            },

            getPlacementTooltip(placement) {
                if (placement.code === 'LoftAdjustment') {
                    return this.$t('ADJUST_LOFT_TOOLTIP');
                }

                if (placement.code === 'LeanAdjustment') {
                    return this.$t('ADJUST_LEAN_TOOLTIP');
                }

                if (placement.code === 'LieAdjustment') {
                    return this.$t('ADJUST_LIE_TOOLTIP');
                }

                if (placement.code === 'WeightAdjustment') {
                    return this.$t('ADJUST_WEIGHT_TOOLTIP');
                }

                return null;
            },
        },
    };
</script>
